import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Collapse,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Storage as DatabaseIcon,
  Description as FileTextIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  MenuBook as BookOpenIcon,
  Group as UsersIcon,
  Event as CalendarIcon,
  Work as BriefcaseIcon
} from "@mui/icons-material";

const SourcesPanel = ({ sources }) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);

  if (!sources || sources.length === 0) {
    return null;
  }

  const getSourceIcon = (type) => {
    switch (type) {
      case "database":
      case "postgresql":
        return <DatabaseIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
      case "document":
      case "documentation":
        return <FileTextIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />;
      case "employees":
        return <UsersIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />;
      case "leaves":
        return <CalendarIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />;
      case "job_offers":
        return <BriefcaseIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
      default:
        return <InfoIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />;
    }
  };

  const getSourceColor = (type) => {
    switch (type) {
      case "database":
      case "postgresql":
        return { bg: theme.palette.success.light + '20', border: theme.palette.success.light, hover: theme.palette.success.main };
      case "document":
      case "documentation":
        return { bg: theme.palette.info.light + '20', border: theme.palette.info.light, hover: theme.palette.info.main };
      case "employees":
        return { bg: theme.palette.secondary.light + '20', border: theme.palette.secondary.light, hover: theme.palette.secondary.main };
      case "leaves":
        return { bg: theme.palette.warning.light + '20', border: theme.palette.warning.light, hover: theme.palette.warning.main };
      case "job_offers":
        return { bg: theme.palette.error.light + '20', border: theme.palette.error.light, hover: theme.palette.error.main };
      default:
        return { bg: theme.palette.grey[50], border: theme.palette.grey[200], hover: theme.palette.grey[300] };
    }
  };

  const getSourceLabel = (type) => {
    const labels = {
      database: "PostgreSQL",
      postgresql: "PostgreSQL",
      document: "Documentation",
      documentation: "Documentation",
      employees: "Employés",
      leaves: "Congés",
      job_offers: "Offres d'emploi",
    };
    return labels[type] || type;
  };

  return (
    <Card elevation={0} sx={{ mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <CardHeader
        sx={{ 
          p: 2, 
          bgcolor: 'grey.50', 
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.100' }
        }}
        onClick={() => setIsExpanded(!isExpanded)}
        avatar={<BookOpenIcon color="primary" fontSize="small" />}
        title={
          <Typography variant="body2" fontWeight={600} color="text.primary">
            Sources utilisées ({sources.length})
          </Typography>
        }
        action={
          <IconButton size="small" sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s'
          }}>
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        }
      />

      <Collapse in={isExpanded}>
        <CardContent sx={{ p: 2, pt: 1, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sources.map((source, index) => {
              const colors = getSourceColor(source.type);
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: colors.border,
                    bgcolor: colors.bg,
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      borderColor: colors.hover,
                      boxShadow: theme.shadows[1]
                    }
                  }}
                >
                  <Box sx={{ mt: 0.5, flexShrink: 0 }}>
                    {getSourceIcon(source.type)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {getSourceLabel(source.type)}
                      </Typography>
                      {source.module && (
                        <Typography variant="caption" color="text.secondary">
                          • {source.module}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {source.name}
                    </Typography>
                    {source.excerpt && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mt: 1 
                        }}
                      >
                        {source.excerpt}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default SourcesPanel;
